/**
 * Paso1SeleccionarRuta.jsx
 * ─────────────────────────────────────────────────────────────
 * Paso 1: cliente → ticket → ruta → tipos de cambio → estructuras.
 *
 * La sección de estructuras solo aparece cuando el tipo de cambio
 * seleccionado lo requiere (no aplica para "Cambio de unidad" puro).
 *
 * Props: { state, dispatch, catalogs }
 */

import React, {memo, useMemo, useCallback, useState} from "react";
import { Divider } from "primereact/divider";
import { Tag     } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";

import {
    CmrCardTitle,
    CmrDropdownField,
    CmrFieldLabel,
    CmrHelpText,
    CmrInfoBanner,
    CmrInfoRow, CmrInputField,
    CmrMultiSelectField,
    errorCorreo,
} from "../cmrShared";

import {
    CHANGE_IDS,
    hasChange,
    hasTrazoCambio,
    needsEstructuras,
    hasFrecuencia,
    FRECUENCIA_MODOS,
    FRECUENCIA_MODO_LABELS,
} from "../hooks/ChangeTypes";
import {InputText} from "primereact/inputtext";
import {Calendar} from "primereact/calendar";

// ─── ÁTOMO: tarjeta de estructura seleccionable ───────────────────────────────

const EstructuraCard = memo(({ estructura, seleccionada, onToggle }) => (
    <div
        role="checkbox"
        aria-checked={seleccionada}
        tabIndex={0}
        className={`cmr-estructura-card flex align-items-start gap-3 p-3${seleccionada ? " cmr-estructura-card--selected" : ""}`}
        onClick={() => onToggle(estructura.id)}
        onKeyDown={(e) => e.key === " " && onToggle(estructura.id)}
    >
        <div
            className={`cmr-checkbox-box flex align-items-center justify-content-center mt-1${seleccionada ? " cmr-checkbox-box--checked" : ""}`}
            aria-hidden="true"
        >
            {seleccionada && <i className="pi pi-check" style={{ fontSize: "0.75rem" }} />}
        </div>

        <div className="flex-1">
            <p className="font-bold text-color mb-2 mt-0">{estructura.nombre}</p>
            <div className="flex gap-2 flex-wrap">
                <Tag value={`${estructura.horaInicio} – ${estructura.horaFin}`} icon="pi pi-clock"    severity="info"    />
                <Tag value={estructura.dias.join(", ")}                         icon="pi pi-calendar" severity="success" />
                <Tag value={estructura.tipoServicio}                            icon="pi pi-tag"      severity="warning" />
            </div>
        </div>
    </div>
));
EstructuraCard.displayName = "EstructuraCard";

// ─── Opciones para "¿qué hacer con este turno?" (solo modo AMBAS) ────────────

const ESTRUCTURA_ACCION_OPTIONS = [
    { label: "Editar existente", value: "editar" },
    { label: "Generar copia",    value: "copia"  },
    { label: "Ambas",            value: "ambas"  },
];

// ─── PASO 1 ───────────────────────────────────────────────────────────────────

const Paso1SeleccionRuta = memo(({ state, dispatch, catalogs }) => {
    const { clienteId, ticketId, rutaId, changeType, fechaTentativa, frecuenciaModo, estructurasSeleccionadas, estructurasAccion, correo } = state;
    const { clientes, tickets, rutas, cambios } = catalogs;
    const [showTicketDetail, setShowTicketDetail] = useState(false);

    const rutaSeleccionada = useMemo(
        () => rutas.data.find((r) => r.id === rutaId) ?? null,
        [rutas.data, rutaId]
    );

    const ticketSeleccionado = useMemo(
        () => tickets.data.find(t => t.id === ticketId) ?? null,
        [tickets.data, ticketId]
    );

    const todasSeleccionadas = rutaSeleccionada?.estructuras.length > 0 &&
        rutaSeleccionada.estructuras.every((e) => estructurasSeleccionadas.includes(e.id));

    const handleToggleTodas = useCallback(() => {
        const payload = todasSeleccionadas
            ? []
            : rutaSeleccionada.estructuras.map((e) => e.id);
        dispatch({ type: "SET_ESTRUCTURAS", payload });
    }, [dispatch, todasSeleccionadas, rutaSeleccionada]);

    // ── Flags derivados del tipo de cambio ───────────────────────────────────
    const esFrecuencia       = hasFrecuencia(changeType);
    const mostrarEstructuras = rutaSeleccionada && (
        needsEstructuras(changeType) ||
        (esFrecuencia && frecuenciaModo) // frecuencia siempre necesita elegir estructuras
    );
    const esSoloCambioUnidad = changeType.length > 0 && !needsEstructuras(changeType) && !esFrecuencia && hasChange(changeType, CHANGE_IDS.UNIDAD);
    const tieneTrazo         = hasTrazoCambio(changeType);

    /**
     * Retorna los textos de cambios activos de forma dinámica
     */
    const getCambiosTrazo = (changeType) => {
        const cambios = [];

        if (hasChange(changeType, CHANGE_IDS.EXTENSION)) {
            cambios.push("Extensión de trazo");
        }

        if (hasChange(changeType, CHANGE_IDS.REDUCCION)) {
            cambios.push("Reducción de trazo");
        }

        if (hasChange(changeType, CHANGE_IDS.PARADAS)) {
            cambios.push("Modificar paradas");
        }

        return cambios;
    };

    const cambiosTrazo = getCambiosTrazo(changeType);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleCliente          = useCallback((e) => dispatch({ type: "SET_CLIENTE",        payload: e.value }), [dispatch]);
    const handleTicket           = useCallback((e) => dispatch({ type: "SET_TICKET",         payload: e.value }), [dispatch]);
    const handleRuta             = useCallback((e) => dispatch({ type: "SET_RUTA",           payload: e.value }), [dispatch]);
    const handleChangeType       = useCallback((e) => dispatch({ type: "SET_CHANGE_RUTA",    payload: e.value }), [dispatch]);
    const handleFechaTentativa   = useCallback((e) => dispatch({ type: "SET_FECHA_TENTATIVA", payload: e.value }), [dispatch]);
    const handleToggleEstructura = useCallback((id) => dispatch({ type: "TOGGLE_ESTRUCTURA", payload: id      }), [dispatch]);
    const handleFrecuenciaModo   = useCallback((modo) => dispatch({ type: "SET_FRECUENCIA_MODO", payload: modo }), [dispatch]);
    const handleCorreo           = useCallback((e) => dispatch({ type: "SET_CORREO", payload: e.target.value }), [dispatch]);
    const handleEstructuraAccion = useCallback(
        (id, accion) => dispatch({ type: "SET_ESTRUCTURA_ACCION", payload: { id, accion } }),
        [dispatch]
    );



    return (
        <>
            <CmrInfoBanner severity="info">
                <strong>Paso 1: Selecciona la ruta a modificar.</strong>
                <span className="ml-1">
                    Elige el cliente, ticket, ruta y el tipo de cambio que deseas realizar.
                </span>
            </CmrInfoBanner>

            {/* ── Identificación del servicio ────────────────────────────── */}
            <div className="cmr-card p-4">
                <CmrCardTitle
                    icon="pi-building"
                    title="Identificación del servicio"
                    subtitle="Selecciona el cliente, ticket y ruta a modificar"
                />

                <div className="grid">
                    <div className="col-12 md:col-4">
                        <CmrDropdownField
                            label="Cliente"
                            required
                            tooltip="Empresa a la que pertenece la ruta"
                            helpText="Escoge la empresa dueña de la ruta"
                            helpIcon="pi-search"
                            catalog={clientes}
                            value={clienteId}
                            onChange={handleCliente}
                            placeholder="Selecciona un cliente..."
                            filter={true}
                        />
                    </div>

                    <div className="col-12 md:col-4 grid">
                        <div className="col-10">
                            <CmrDropdownField
                                label="Ticket asociado"
                                required
                                tooltip="Ticket de soporte relacionado"
                                helpText="Ticket relacionado con el cambio solicitado"
                                helpIcon="pi-ticket"
                                catalog={tickets}
                                value={ticketId}
                                onChange={handleTicket}
                                placeholder={clienteId ? "Selecciona un ticket..." : "Primero elige un cliente"}
                                disabled={!clienteId}
                            />
                        </div>

                        {ticketSeleccionado && (
                            <div className='col-2 flex align-items-center'>
                                <Button
                                    icon="pi pi-eye"
                                    className="p-button-outlined p-button-secondary mb-3"
                                    tooltip="Ver detalle del ticket"
                                    onClick={() => setShowTicketDetail(true)}
                                />
                            </div>

                        )}
                    </div>

                    <div className="col-12 md:col-4">
                        <CmrDropdownField
                            label="Ruta a modificar"
                            required
                            tooltip="Ruta que se va a modificar"
                            helpText="Ruta que deseas extender, reducir o cambiar"
                            helpIcon="pi-map"
                            catalog={rutas}
                            value={rutaId}
                            filter
                            onChange={handleRuta}
                            placeholder={ticketId ? "Selecciona una ruta..." : "Primero elige un ticket"}
                            disabled={!ticketId}
                        />
                    </div>
                </div>

                {/* ── Detalle de la ruta + tipo de cambio ─────────────────── */}
                {rutaSeleccionada && (
                    <>
                        <Divider/>
                        <div className="grid">

                            {/* Tipo de cambio (MultiSelect) */}
                            <div className="col-12 md:col-4">
                                <CmrMultiSelectField
                                    label="Tipo de cambio"
                                    required
                                    tooltip="Selecciona los cambios que se realizarán a la ruta"
                                    helpText="Puedes combinar varios tipos de cambio"
                                    helpIcon="pi-list"
                                    catalog={cambios}
                                    value={changeType}
                                    onChange={handleChangeType}
                                    display="chip"
                                    placeholder="¿Qué deseas modificar?"
                                />
                            </div>

                            {/* Tipo de ruta (solo lectura)
                            <div className="col-12 md:col-3 cmr-field-group">
                                <CmrFieldLabel tooltip="Tipo de ruta asignado">
                                    Tipo de ruta
                                </CmrFieldLabel>
                                <CmrInfoRow>
                                    <i className="pi pi-map-marker" aria-hidden="true"/>
                                    <span className="font-bold text-color">{rutaSeleccionada.tipoRuta}</span>
                                </CmrInfoRow>
                                <CmrHelpText icon="pi-lock">
                                    El tipo de ruta se obtiene del sistema y no puede modificarse aquí
                                </CmrHelpText>
                            </div>*/}

                            <div className="col-12 md:col-4 cmr-field-group">
                                <CmrFieldLabel
                                    required
                                    tooltip="Fecha en la que se ejecutarán los cambios en la ruta"
                                >
                                    Fecha tentativa de ejecución
                                </CmrFieldLabel>

                                <Calendar
                                    id="fechaTentativa"
                                    value={fechaTentativa ?? ""}
                                    onChange={handleFechaTentativa}
                                    dateFormat="dd/mm/yy"
                                    placeholder="Seleccione la fecha"
                                    showIcon
                                    icon="pi pi-calendar"
                                    minDate={new Date()}
                                    className="w-full"
                                />

                                <CmrHelpText icon="pi-calendar">
                                    Fecha aproximada en que se aplicarán los cambios solicitados
                                </CmrHelpText>
                            </div>

                            <div className="col-12 md:col-4 cmr-field-group">
                                <CmrInputField
                                    required={true}
                                    label="Correo de notificación"
                                    tooltip="Correo al que se enviará la cotizacion"
                                    placeholder="ejemplo@correo.com"
                                    value={correo ?? ""}
                                    onChange={handleCorreo}
                                    error={errorCorreo(correo)}
                                    helpText="Se enviará un aviso cuando la cotizacion se haya costeado"
                                    helpIcon="pi-envelope"
                                />
                            </div>

                            {/* Unidad actual (solo lectura) */}
                            <div className="col-12 md:col-3 cmr-field-group">
                                <CmrFieldLabel tooltip="Vehículo actualmente asignado a esta ruta">
                                    Unidad actualmente asignada
                                </CmrFieldLabel>
                                <CmrInfoRow>
                                    <i className="pi pi-truck" aria-hidden="true"/>
                                    <span className="font-bold text-color">{rutaSeleccionada.unidadActual.nombre}</span>
                                    <span
                                        className="text-color-secondary">— {rutaSeleccionada.unidadActual.capacidad}</span>
                                    <Tag value="Actual" severity="success"/>
                                </CmrInfoRow>
                                <CmrHelpText icon="pi-info-circle">
                                    {hasChange(changeType, CHANGE_IDS.UNIDAD)
                                        ? "Podrás seleccionar una nueva unidad en el siguiente paso"
                                        : "Selecciona «Cambio de unidad» si deseas modificarla"}
                                </CmrHelpText>
                            </div>
                        </div>

                        {/* Aviso: solo cambio de unidad → no se necesitan estructuras */}
                        {esSoloCambioUnidad && (
                            <CmrInfoBanner severity="warn">
                                <strong>Cambio de unidad:</strong> este tipo de cambio aplica a toda la ruta
                                y no requiere seleccionar estructuras. Puedes continuar al siguiente paso.
                            </CmrInfoBanner>
                        )}

                        {/* Aviso: trazo → redirigirá al editor */}
                        {tieneTrazo && cambiosTrazo.length > 0 && (
                            <CmrInfoBanner severity="warn">
                                <strong>
                                    {cambiosTrazo.join(" y ")} seleccionad{cambiosTrazo.length > 1 ? "as" : "a"}:
                                </strong>
                                <span className="ml-1">
                                    Al guardar la cotización serás redirigido al editor de trazo
                                    para completar el recorrido en el mapa.
                                </span>
                            </CmrInfoBanner>
                        )}

                        {/* Selector de modo de frecuencia */}
                        {esFrecuencia && (
                            <div className="mt-3">
                                <CmrInfoBanner severity="info">
                                    <strong>Cambios en la frecuencia:</strong>{" "}
                                    selecciona cómo deseas realizar los cambios en la frecuencia.
                                </CmrInfoBanner>

                                <div className="flex flex-column gap-2" role="radiogroup" aria-label="Modo de frecuencia">
                                    {Object.entries(FRECUENCIA_MODOS).map(([key, modo]) => {
                                        const seleccionado = frecuenciaModo === modo;
                                        return (
                                            <div
                                                key={key}
                                                role="radio"
                                                aria-checked={seleccionado}
                                                tabIndex={0}
                                                className={`cmr-estructura-card flex align-items-center gap-3 p-3${seleccionado ? " cmr-estructura-card--selected" : ""}`}
                                                onClick={() => handleFrecuenciaModo(modo)}
                                                onKeyDown={(e) => e.key === " " && handleFrecuenciaModo(modo)}
                                            >
                                                <div
                                                    className={`cmr-checkbox-box flex align-items-center justify-content-center${seleccionado ? " cmr-checkbox-box--checked" : ""}`}
                                                    aria-hidden="true"
                                                >
                                                    {seleccionado && <i className="pi pi-check" style={{ fontSize: "0.75rem" }} />}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-color m-0">{FRECUENCIA_MODO_LABELS[modo]}</p>
                                                    <p className="text-sm text-color-secondary m-0 mt-1">
                                                        {modo === FRECUENCIA_MODOS.MODIFICAR && "Editar los horarios/días de las estructuras existentes sin crear nuevas."}
                                                        {modo === FRECUENCIA_MODOS.NUEVA && "Se copiará cada estructura seleccionada para editarla sin alterar la original."}
                                                        {modo === FRECUENCIA_MODOS.AMBAS && "Podrás modificar las estructuras existentes y además generar copias nuevas."}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>



            {/* ── Selección de estructuras (condicional) ─────────────────── */}
            {mostrarEstructuras && (
                <div className="cmr-card p-4">
                    <CmrCardTitle
                        icon="pi-clock"
                        title="¿Qué estructuras deseas modificar?"
                        subtitle={
                            esFrecuencia && frecuenciaModo === FRECUENCIA_MODOS.NUEVA
                                ? "Las estructuras seleccionadas se copiarán para crear nuevas versiones"
                                : esFrecuencia && frecuenciaModo === FRECUENCIA_MODOS.AMBAS
                                    ? "Por cada turno que selecciones, indica si se edita, se copia o ambas cosas"
                                    : "Selecciona solo las estructuras que vas a editar"
                        }
                    />
                    <CmrInfoBanner severity="info">
                        <strong>No es obligatorio seleccionar todas las estructuras.</strong>{" "}
                        Es conveniente marcar solo las que vas a cambiar. Las que no selecciones{" "}
                        <strong>no se borrarán</strong>: se quedarán igual como están ahorita en la ruta, sin cambios
                        en sus horarios, días ni recorridos.
                    </CmrInfoBanner>

                    {/* Seleccionar todas */}
                    <div
                        className="flex align-items-center gap-2 mb-2 cursor-pointer"
                        role="checkbox"
                        aria-checked={todasSeleccionadas}
                        tabIndex={0}
                        onClick={handleToggleTodas}
                        onKeyDown={(e) => e.key === " " && handleToggleTodas()}
                    >
                        <div
                            className={`cmr-checkbox-box flex align-items-center justify-content-center${todasSeleccionadas ? " cmr-checkbox-box--checked" : ""}`}
                            aria-hidden="true"
                        >
                            {todasSeleccionadas && <i className="pi pi-check" style={{fontSize: "0.75rem"}}/>}
                        </div>
                        <span className="text-sm font-bold text-color">
                            {todasSeleccionadas ? "Deseleccionar todas" : "Seleccionar todas"}
                        </span>
                    </div>

                    <div className="flex flex-column gap-2" role="group" aria-label="Estructuras de la ruta">
                        {rutaSeleccionada.estructuras.map((est) => {
                            const seleccionada = estructurasSeleccionadas.includes(est.id);
                            return (
                                <div key={est.id} className="flex flex-column gap-2">
                                    <EstructuraCard
                                        estructura={est}
                                        seleccionada={seleccionada}
                                        onToggle={handleToggleEstructura}
                                    />

                                    {/* Solo en modo AMBAS: elegir si este turno se edita, se copia o ambas cosas */}
                                    {seleccionada && esFrecuencia && frecuenciaModo === FRECUENCIA_MODOS.AMBAS && (
                                        <div className="ml-5 mb-2">
                                            <span className="text-sm text-color-secondary block mb-1">
                                                ¿Qué hacer con este turno?
                                            </span>
                                            <SelectButton
                                                value={estructurasAccion[est.id] ?? "ambas"}
                                                onChange={(e) => e.value && handleEstructuraAccion(est.id, e.value)}
                                                options={ESTRUCTURA_ACCION_OPTIONS}
                                                optionLabel="label"
                                                optionValue="value"
                                                allowEmpty={false}
                                                aria-label={`Acción para el turno ${est.nombre}`}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {estructurasSeleccionadas.length > 0 && (
                        <div className="mt-3">
                            <CmrInfoBanner severity="success">
                                <strong>{estructurasSeleccionadas.length} estructura(s) seleccionada(s)</strong> para
                                modificar
                            </CmrInfoBanner>
                        </div>
                    )}
                </div>
            )}

            <Dialog
                header="Detalle del ticket"
                visible={showTicketDetail}
                style={{ width: "450px" }}
                modal
                onHide={() => setShowTicketDetail(false)}
            >
                {ticketSeleccionado && (
                    <div className="flex flex-column gap-3">

                        <div>
                            <span className="text-sm text-color-secondary">Nombre</span>
                            <p className="m-0 font-bold">{ticketSeleccionado.nombre}</p>
                        </div>

                        <div>
                            <span className="text-sm text-color-secondary">Descripción</span>
                            <p className="m-0" style={{ whiteSpace: "pre-line" }}>
                                {ticketSeleccionado.descripcion}
                            </p>
                        </div>

                        <div className="flex align-items-center gap-2">
                            <span className="text-sm text-color-secondary">Prioridad:</span>
                            <Tag
                                value={ticketSeleccionado.prioridad}
                                severity={
                                    ticketSeleccionado.prioridad === "Alta"
                                        ? "danger"
                                        : ticketSeleccionado.prioridad === "Media"
                                            ? "warning"
                                            : "success"
                                }
                            />
                        </div>

                    </div>
                )}
            </Dialog>
        </>
    );
});

Paso1SeleccionRuta.displayName = "Paso1SeleccionRuta";
export default Paso1SeleccionRuta;
